import { getScheduleForDay } from "./ScheduleUtils";
import { _toISOString } from "../../../helper/dateHelper";
import { fetchEmployeeSchedulesBatch } from "../ScheduleFetcher";
import { fetchEmployeeTwentyFourSevenBatch } from "../TwentyFourSevenFetcher";

export const buildLogLookupMap = (logs) => {
  const lookupMap = new Map();
  
  for (const log of logs) {
    if (!log?.date) continue;
    
    const key = `${String(log.userID)}:${log.date}`;
    if (!lookupMap.has(key)) {
      lookupMap.set(key, log);
    }
  }
  
  return lookupMap;
};

export const shouldSkipDate = (date, { dateSet, isTwentyFourSeven = false, check24Seven = false }) => {
  if (!date || !dateSet) return false;
  if (check24Seven && isTwentyFourSeven) return false;
  return dateSet.has(date);
};

export const hasFullDaySuspension = (suspensionRangesByDate, logDate, schedTimeInMin, schedTimeOutMin) => {
  if (!suspensionRangesByDate) {
    return false;
  }

  if (schedTimeInMin == null || schedTimeOutMin == null) {
    return false;
  }

  const timeRanges = suspensionRangesByDate.get(logDate);
  if (!timeRanges?.length) {
    return false;
  }

  for (const { startMin, endMin } of timeRanges) {
    if (startMin <= schedTimeInMin && endMin >= schedTimeOutMin) {
      return true;
    }
  }

  return false;
};

export const getNextLog = (currentLog, logLookupMap) => {
  if (!currentLog?.date || !currentLog?.userID) {
    return null;
  }

  const currentDate = new Date(currentLog.date);
  const nextDate = new Date(currentDate);
  nextDate.setDate(nextDate.getDate() + 1);

  const nextDateStr =
    nextDate.getFullYear() + "-" +
    String(nextDate.getMonth() + 1).padStart(2, "0") + "-" +
    String(nextDate.getDate()).padStart(2, "0");

  const currentUserID = String(currentLog.userID);
  const lookupKey = `${currentUserID}:${nextDateStr}`;

  return logLookupMap.get(lookupKey) || null;
};

export const parseTimeToMinutes = (time) => {
  if (!time || time === "--:--") return 0;
  const parts = time.split(":").map(Number);
  const hour = parts[0] ?? 0;
  const min = parts[1] ?? 0;
  const val = hour * 60 + min;
  return isNaN(val) ? null : val;
};

export const isMorningClockTime = (timeStr) => {
  if (!timeStr || timeStr === "--:--") return false;
  const mins = parseTimeToMinutes(timeStr);
  return mins != null && mins > 0 && mins < 12 * 60;
};

export const createEmployeeStats = (dateRangeCount, isArrayDate) => {
  if (isArrayDate) {
    return {
      totalLateUTMins: Array(dateRangeCount).fill(0),
      absentCount: Array(dateRangeCount).fill(0),
      halfDayCount: Array(dateRangeCount).fill(0),
      lateCount: Array(dateRangeCount).fill(0),
      minutesIncurred: Array(dateRangeCount).fill(null).map(() => []),
      absentDates: Array(dateRangeCount).fill(null).map(() => new Set()), 
      halfDayDates: Array(dateRangeCount).fill(null).map(() => new Set()),
      lateDates: Array(dateRangeCount).fill(null).map(() => new Set()),
    };
  } else {
    return {
      totalLateUTMins: 0,
      absentCount: 0,
      halfDayCount: 0,
      lateCount: 0,
      minutesIncurred: [],
      absentDates: new Set(), 
      halfDayDates: new Set(),
      lateDates: new Set(),
    };
  }
};

const getRangeBounds = (range, useTagging = false) => {
  const query = useTagging
    ? range?.taggingDateRangeQuery ?? range?.dateRangeQuery
    : range?.dateRangeQuery;
  return {
    startDate: query?.$gte,
    endDate: query?.$lte,
  };
};

export const isDateInSelectedRanges = (dateStr, parsedDateRanges) => {
  if (!dateStr || !parsedDateRanges?.length) return true;

  for (const range of parsedDateRanges) {
    const { startDate, endDate } = getRangeBounds(range, false);
    if (startDate && endDate && dateStr >= startDate && dateStr <= endDate) {
      return true;
    }
  }

  return false;
};

export const isDateInTaggingRanges = (dateStr, parsedDateRanges) => {
  if (!dateStr || !parsedDateRanges?.length) return true;

  for (const range of parsedDateRanges) {
    const { startDate, endDate } = getRangeBounds(range, true);
    if (startDate && endDate && dateStr >= startDate && dateStr <= endDate) {
      return true;
    }
  }

  return false;
};

export const getDateRangeIndex = (dateStr, parsedDateRanges, useTagging = true) => {
  if (!dateStr || !parsedDateRanges || parsedDateRanges.length === 0) return 0;
  
  for (let i = 0; i < parsedDateRanges.length; i++) {
    const { startDate, endDate } = getRangeBounds(parsedDateRanges[i], useTagging);
    if (startDate && endDate && dateStr >= startDate && dateStr <= endDate) {
      return i;
    }
  }
  return -1;
};

export const isMonday = (dateStr) => {
  if (!dateStr) return false;
  const date = new Date(dateStr);
  return date.getDay() === 1;
};

export const isGraveyardSchedule = (scheduleForDay, scheduleToUse) => {
  if (!scheduleForDay && !scheduleToUse) return false;
  return (
    scheduleForDay?.day?.includes(" - ") ||
    scheduleForDay?.day?.includes("Graveyard") ||
    scheduleToUse?.selectedOption?.includes("Graveyard")
  );
};


export const isLateAfter7AM = (actualTimeInMins, isGraveyard, schedInMins) => {
  if (actualTimeInMins == null) return false;
  
  if (!isGraveyard) {
    // For regular shifts, simply check if actual time in > 420 (7:00 AM)
    return actualTimeInMins > 420;
  }
  
  // For graveyard shifts
  if (schedInMins != null && schedInMins > 420) {
    // Example: schedInMins = 1320 (10:00 PM), actualTimeInMins = 430 (7:10 AM next day)
    // 430 < 1320 (next day) AND 430 > 420 (exceeds 7:00 AM) -> return true
    // Example: schedInMins = 1320, actualTimeInMins = 1350 (10:30 PM same day)
    // 1350 >= 1320 (same day) -> return false
    return actualTimeInMins < schedInMins && actualTimeInMins > 420;
  } else {
    // Schedule time in is early (e.g., before 7:00 AM or early morning)
    // For graveyard, this is less common, but check normally
    return actualTimeInMins > 420;
  }
};


export const getSuspensionOverlapMins = (
  suspensionRangesByDate,
  logDate,
  rangeStartMin,
  rangeEndMin,
  nextLogDate
) => {
  if (!suspensionRangesByDate) {
    return 0;
  }

  if (rangeStartMin == null || rangeEndMin == null) {
    return 0;
  }
  
  let totalOverlap = 0;

  const calculateOverlapForDate = (dateStr) => {
    const timeRanges = suspensionRangesByDate.get(dateStr);
    if (!timeRanges?.length) return 0;

    let overlap = 0;
    for (const { startMin, endMin } of timeRanges) {
      const overlapStart = Math.max(rangeStartMin, startMin);
      const overlapEnd = Math.min(rangeEndMin, endMin);

      if (overlapStart < overlapEnd) {
        overlap += overlapEnd - overlapStart;
      }
    }
    return overlap;
  };

  if (rangeStartMin < rangeEndMin) {
    // For graveyard shifts: if both times are early morning (< 720 = 12:00 PM) 
    // and we have a nextLogDate, the range is actually on the next day
    const isEarlyMorningRange = rangeStartMin < 720 && rangeEndMin < 720;
    const dateToCheck = isEarlyMorningRange && nextLogDate ? nextLogDate : logDate;
    totalOverlap += calculateOverlapForDate(dateToCheck);
  } else if (nextLogDate) {
    const originalStart = rangeStartMin;
    const originalEnd = rangeEndMin;

    rangeStartMin = originalStart;
    rangeEndMin = 1440;
    
    totalOverlap += calculateOverlapForDate(logDate);

    rangeStartMin = 0;
    rangeEndMin = originalEnd;
    totalOverlap += calculateOverlapForDate(nextLogDate);
  }

  return totalOverlap;
};

const getEffectiveScheduleForDate = (employeeSchedules, dateStr) => {
  if (!employeeSchedules?.length) return null;
  
  for (const schedule of employeeSchedules) {
    if (!schedule.createdDateStr) continue;
    if (schedule.createdDateStr <= dateStr) {
      return schedule;
    }
  }
  
  return null;
};

const getEffectiveTwentyFourSevenForDate = (assignments, dateStr) => {
  if (!assignments?.length) return null;
  
  for (const assignment of assignments) {
    if (!assignment.createdDateStr) continue;
    if (assignment.createdDateStr <= dateStr) {
      return assignment;
    }
  }
  
  return null;
};

export const preprocessLogs = async (logs, lookupData, logLookupMap, employeeIdMap, parsedDateRanges = []) => {
  const preprocessedLogs = [...logs];
  const virtualLogsAdded = new Set();
  
  const scheduleCache = {
    byEmployee: new Map(), 
  };
  
  const twentyFourSevenCache = {
    byEmployee: new Map(),
  };
  
  const employeeMaxDates = new Map();
  const userIDMaxDates = new Map();
  
  for (const log of logs) {
    if (!log.date || !log.userID) continue;
    
    const employeeId = employeeIdMap.get(log.userID);
    // const workingDays = lookupData[log.userID]?.workingDaysSet;

    if (!employeeId) continue;
    
    const currentMax = employeeMaxDates.get(employeeId);
    if (!currentMax || log.date > currentMax) {
      employeeMaxDates.set(employeeId, log.date);
      userIDMaxDates.set(log.userID, log.date);
    }
  }
  
  const SCHEDULE_247_BATCH = 200;
  const employeeIds = Array.from(employeeMaxDates.keys());
  if (employeeIds.length) {
    const globalMaxDateStr = Array.from(employeeMaxDates.values()).reduce((max, d) =>
      !max || d > max ? d : max,
    );
    await Promise.all([
      fetchEmployeeSchedulesBatch(employeeIds, globalMaxDateStr, scheduleCache, SCHEDULE_247_BATCH),
      fetchEmployeeTwentyFourSevenBatch(
        employeeIds,
        globalMaxDateStr,
        twentyFourSevenCache,
        SCHEDULE_247_BATCH,
      ),
    ]);
  }

  // const PREFETCH_CONCURRENCY = 25;
  // const employeeEntries = Array.from(employeeMaxDates.entries());

  // await runWithConcurrencyLimit(employeeEntries, PREFETCH_CONCURRENCY, async ([employeeId, maxDate]) => {
  //   await Promise.all([
  //     fetchEmployeeSchedules(employeeId, maxDate, scheduleCache),
  //     fetchEmployeeTwentyFourSeven(employeeId, maxDate, twentyFourSevenCache),
  //   ]);
  // });
  
  const getScheduleForDate = (employeeId, dateStr) => {
    if (!employeeId || !dateStr) return null;
    
    const employeeSchedules = scheduleCache.byEmployee.get(employeeId);
    if (employeeSchedules) {
      return getEffectiveScheduleForDate(employeeSchedules, dateStr);
    }
    
    return null;
  };
  
  const getTwentyFourSevenForDate = (employeeId, dateStr) => {
    if (!employeeId || !dateStr) return null;
    
    const assignments = twentyFourSevenCache.byEmployee.get(employeeId);
    if (assignments) {
      return getEffectiveTwentyFourSevenForDate(assignments, dateStr);
    }
    
    return null;
  };

  for (let i = 0; i < preprocessedLogs.length; i++) {
    const log = preprocessedLogs[i];
    
    if (!log.date || !log.userID) {
      continue;
    }

    const maxDateForUser = userIDMaxDates.get(log.userID);
    if(maxDateForUser &&  log.date === maxDateForUser){
      log.maxDate = true;
    }
    
    const employeeId = employeeIdMap.get(log.userID);
    const effectiveSchedule = employeeId ? getScheduleForDate(employeeId, log.date) : null;
    log.effectiveSchedule =
      log.scheduleUpdate || effectiveSchedule || lookupData[log.userID]?.schedule || null;
    
    // Fetch and assign twenty_fourSeven_assignment
    const effectiveTwentyFourSeven = employeeId ? getTwentyFourSevenForDate(employeeId, log.date) : null;
    if (effectiveTwentyFourSeven) {
      log.twentyFourSeven = log.twentyFourSeven || effectiveTwentyFourSeven.name || null;
    }
    
    if (!log.effectiveSchedule) {
      continue;
    }
    
    const stampDate = log.stampDate ? new Date(log.stampDate) : null;
    if (!stampDate) continue;
    
    const scheduleToUse =
      log.scheduleUpdate || log.effectiveSchedule || lookupData[log.userID]?.schedule;
    const scheduleForDay = getScheduleForDay(scheduleToUse, stampDate);
    const isGraveyard = isGraveyardSchedule(scheduleForDay, scheduleToUse);
    if (isGraveyard) {
      log.isGraveyard = true;
    }

    if (isGraveyard && log.timeAmOut !== "--:--") {
      const currentDate = new Date(log.date);
      const prevDate = new Date(currentDate);
      prevDate.setDate(prevDate.getDate() - 1);
      
      const prevDateStr = _toISOString(prevDate);

      if (isDateInTaggingRanges(prevDateStr, parsedDateRanges)) {
        const lookupKey = `${String(log.userID)}:${prevDateStr}`;
        
        const previousLog = logLookupMap.get(lookupKey) || null;
        
        if (!previousLog) {
          if (!virtualLogsAdded.has(lookupKey)) {
            // Get schedule for previous date (uses cache)
            const prevScheduleToUse = employeeId 
              ? getScheduleForDate(employeeId, prevDateStr) || lookupData[log.userID]?.schedule
              : lookupData[log.userID]?.schedule;
            
            const prevStampDate = new Date(prevDateStr + "T00:00:00.000Z");
            const prevScheduleForDay = getScheduleForDay(prevScheduleToUse, prevStampDate);
            
            if (prevScheduleForDay) {
              // Get twenty_fourSeven for previous date
              const prevTwentyFourSeven = employeeId 
                ? getTwentyFourSevenForDate(employeeId, prevDateStr)
                : null;
              
              const virtualIsGraveyard = isGraveyardSchedule(prevScheduleForDay, prevScheduleToUse);
              const virtualLog = {
                userID: log.userID,
                scheduleUpdate: log.scheduleUpdate,
                date: prevDateStr,
                stampDate: prevStampDate,
                timeIn: "--:--",
                timeOut: "--:--",
                timeAmOut: "--:--",
                timePmIn: "--:--",
                timeRecStats: [],
                effectiveSchedule: prevScheduleToUse,
                twentyFourSeven: prevTwentyFourSeven?.name || null,
                isVirtual: true,
              };
              if (virtualIsGraveyard) {
                virtualLog.isGraveyard = true;
              }
              
              // Lookup-only: supports graveyard continuity but must not be stats-processed.
              logLookupMap.set(lookupKey, virtualLog);
              virtualLogsAdded.add(lookupKey);
            }
          }
        }
      }
    }
  }
  
  return preprocessedLogs;
};
